import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { motion as Motion, AnimatePresence } from "motion/react";
import { X } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { OFFER_TONE, type OfferTone } from "@/lib/offer-tone";

const SIZE_STYLES = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-2xl",
};

interface InfoModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  eyebrow?: ReactNode;
  subtitle?: ReactNode;
  /** Gradient & accent header. `neutral` = pas de gradient (header blanc simple). */
  tone?: OfferTone | "neutral";
  size?: "sm" | "md" | "lg" | "xl";
  footer?: ReactNode;
  children: ReactNode;
  className?: string;
}

export function InfoModal({
  open,
  onClose,
  title,
  eyebrow,
  subtitle,
  tone = "neutral",
  size = "md",
  footer,
  children,
  className,
}: InfoModalProps) {
  if (typeof document === "undefined") return null;

  const isToned = tone !== "neutral";
  const t = isToned ? OFFER_TONE[tone] : null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <Motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-slate-950/45 backdrop-blur-sm"
            onClick={onClose}
          />

          <Motion.div
            key="modal"
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.15 }}
            className={cn(
              "relative w-full overflow-hidden rounded-3xl border border-white/20 bg-white shadow-2xl",
              SIZE_STYLES[size],
              className,
            )}
          >
            <header
              className={cn(
                "relative px-7 pb-6 pt-7",
                isToned ? cn(t!.gradient, "text-white") : "bg-white",
              )}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  {eyebrow && (
                    <div
                      className={cn(
                        "mb-2 inline-flex rounded-lg px-2 py-1 text-[9px] font-black uppercase tracking-widest",
                        isToned ? "bg-white/20 text-white" : "bg-indigo-50 text-indigo-600",
                      )}
                    >
                      {eyebrow}
                    </div>
                  )}
                  <h3
                    className={cn(
                      "font-display text-xl font-black uppercase tracking-tight",
                      isToned ? "text-white" : "text-[#003366]",
                    )}
                  >
                    {title}
                  </h3>
                  {subtitle && (
                    <p
                      className={cn(
                        "mt-1 text-[10px] font-black uppercase tracking-widest",
                        isToned ? "text-white/80" : "text-gray-400",
                      )}
                    >
                      {subtitle}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className={cn(
                    "flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition-colors",
                    isToned
                      ? "bg-white/20 text-white hover:bg-white/30"
                      : "text-gray-400 hover:bg-gray-50 hover:text-gray-600",
                  )}
                  aria-label="Fermer"
                >
                  <X size={18} />
                </button>
              </div>
            </header>

            <div className="max-h-[70vh] overflow-y-auto px-7 py-6">{children}</div>

            {footer && (
              <footer className="border-t border-gray-100 bg-gray-50/50 px-7 py-4">{footer}</footer>
            )}
          </Motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
}

/**
 * Row d'info au format icône + label + valeur. À utiliser dans `<InfoModal>` ou ailleurs.
 */
interface InfoModalRowProps {
  icon: LucideIcon;
  label: string;
  children: ReactNode;
  className?: string;
}

export function InfoModalRow({ icon: Icon, label, children, className }: InfoModalRowProps) {
  return (
    <div className={cn("flex items-center gap-3", className)}>
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gray-50">
        <Icon size={16} className="text-gray-400" />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">{label}</p>
        <div className="text-sm font-black text-[#0F172A]">{children}</div>
      </div>
    </div>
  );
}
